import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { handleErrorToast, handleSuccessToast } from 'src/components/molecules';
import {
  formatStateRule,
  StateRule,
} from 'src/components/organisms/RoutingRule/helpers/compareAndFormatRoutingRules';
import {
  ReactStateHook,
  useCheckUnsavedRulesOnLeave,
} from 'src/components/organisms/RoutingRule/hooks';
import {
  RoutingRuleType,
  useCreateRoutingRuleMutation,
  useDeleteRoutingRuleMutation,
  useDisableRuleMutationMutation,
  useUpdateRoutingRuleMutation,
} from 'src/graphql';

interface Parameters {
  setConfirmationModalOpen: (confirmationModalOpen: boolean) => void;
  setConfirmationModalState: (confirmationModalState: {
    title: string;
    description: string;
    onConfirm: () => void;
    onCancel?: () => void;
    confirmButtonText?: string;
  }) => void;
  rule: StateRule;
  onLeavePage?: (checkUnsavedRulesCallback: () => boolean) => void;
  saveRulesBeforeLeaving: boolean;
  setAllRulesAreSaved: ReactStateHook<boolean>;
  featureFlags?: { isRelationshipToOrgEnabled?: boolean };
}

export type DebouncerCallback = () => void;

export const useMutateRoutingRule = ({
  setConfirmationModalOpen,
  setConfirmationModalState,
  rule,
  onLeavePage,
  saveRulesBeforeLeaving,
  setAllRulesAreSaved,
  featureFlags,
}: Parameters) => {
  const queryClient = useQueryClient();

  const duplicateNameModalState = {
    title: 'Rule already exists',
    description:
      'The specified routing rule already exists. Please choose a unique name or modify the existing rule.',
    onConfirm: () => setConfirmationModalOpen(false),
    confirmButtonText: 'OK',
  };

  const debounce = (
    debouncerCallback: DebouncerCallback,
    activateDebouncer: boolean,
  ) => {
    if (activateDebouncer) {
      debouncerCallback();
    }
  };

  const renderSuccessToast = () => {
    handleSuccessToast({
      toastMessage: 'Rule successfully saved.',
    });
  };

  const { mutate: saveRule } = useUpdateRoutingRuleMutation({
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['RoutingRulesPaginated'] });
      queryClient.refetchQueries({ queryKey: ['GetRoutingAuditEntries'] });
      debounce(() => renderSuccessToast(), Boolean(!saveRulesBeforeLeaving));
      setAllRulesAreSaved(true);
    },
    onError: (e) => {
      setAllRulesAreSaved(false);
      if (
        e instanceof Error &&
        e.message === 'Rule with this name already exists'
      ) {
        setConfirmationModalState(duplicateNameModalState);
        setConfirmationModalOpen(true);
      } else {
        handleErrorToast({
          toastMessage: "Rule couldn't be saved.",
        });
      }
    },
  });

  const { mutate: createRule } = useCreateRoutingRuleMutation({
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['RoutingRulesPaginated'] });
      queryClient.refetchQueries({ queryKey: ['GetRoutingAuditEntries'] });
    },
    onError: (e) => {
      if (
        e instanceof Error &&
        e.message === 'Rule with this name already exists'
      ) {
        setConfirmationModalState(duplicateNameModalState);
        setConfirmationModalOpen(true);
      } else {
        handleErrorToast({
          toastMessage: "Rule couldn't be created.",
        });
      }
    },
  });

  const { mutate: deleteRule } = useDeleteRoutingRuleMutation({
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['RoutingRulesPaginated'] });
      queryClient.refetchQueries({ queryKey: ['GetRoutingAuditEntries'] });
    },
  });

  const { mutate: enableRule } = useUpdateRoutingRuleMutation({
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['RoutingRulesPaginated'] });
      queryClient.refetchQueries({ queryKey: ['GetRoutingAuditEntries'] });
      handleSuccessToast({
        toastMessage: 'Rule was enabled.',
      });
    },
    onError: () => {
      handleErrorToast({
        toastMessage: "Rule couldn't be enabled.",
      });
    },
  });

  const { mutate: disableRule } = useDisableRuleMutationMutation({
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['RoutingRulesPaginated'] });
      queryClient.refetchQueries({ queryKey: ['GetRoutingAuditEntries'] });
    },
  });

  const duplicateRule = (rule: RoutingRuleType) => {
    createRule({
      parameters: {
        name: `Duplicated ${rule.name}`,
        destinationGroup: rule.destinationGroup,
        disabled: true,
        priority: 1,
        rule: rule.rule,
        groupAssignment: rule.groupAssignment ?? false,
        isCOI: rule.isCOI,
      },
    });
  };

  const saveCurrentRule = useCallback(() => {
    saveRule({
      parameters: formatStateRule(rule, featureFlags),
    });
  }, [rule, saveRule, featureFlags]);

  useCheckUnsavedRulesOnLeave(
    {
      id: rule.id,
      name: rule.name,
      destinationGroup: rule.destinationGroup,
      priority: rule.priority,
      selectedDataSource: rule.selectedDataSource,
      parameterList: rule.parameterList,
      disabled: rule.disabled,
      groupAssignment: rule.groupAssignment,
    },
    Boolean(onLeavePage),
    saveCurrentRule,
    saveRulesBeforeLeaving,
    onLeavePage,
  );

  return {
    saveRule,
    createRule,
    deleteRule,
    enableRule,
    disableRule,
    duplicateRule,
  };
};
